/**
 * Bloco auxiliar recolhível da tela do laudo (E65.F1.T02 — item 9 do E2E do owner). // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * O que o perito faz na tela é produzir o laudo; homologar e registrar achado // ux-neutrality:allow doc-comment
 * autoral são passos ocasionais que ocupavam altura fixa acima do trabalho. Aqui // ux-neutrality:allow doc-comment
 * eles recolhem — mas o CABEÇALHO nunca some, e é ele que carrega o estado: um // ux-neutrality:allow doc-comment
 * gate pendente atrás de um bloco fechado não pode parecer gate ausente. // ux-neutrality:allow doc-comment
 */
import { useId, type ReactNode } from 'react';
import { Blueprint, CardKicker } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

const t = ptBR.laudoCaso.blocoRecolhivel;

export interface LaudoBlocoRecolhivelProps {
  kicker: string;
  titulo: string;
  /** Estado que continua visível com o bloco fechado (badge de gate, marca de preenchido). */
  acessorio?: ReactNode;
  aberto: boolean;
  onAbertoChange: (aberto: boolean) => void;
  /** Identifica a section externa — permanece no DOM aberta ou fechada. */
  testId: string;
  /** Atributos de estado lidos por teste/E2E na section externa. */
  dataAttrs?: Record<string, string>;
  /**
   * Resultado do que o bloco faz — permanece na tela com o bloco fechado. O que
   * recolhe é o FORMULÁRIO; esconder junto a confirmação e o registro de quem
   * homologou transformaria o recolhimento em perda de informação.
   */
  rodape?: ReactNode;
  children: ReactNode;
}

export function LaudoBlocoRecolhivel({
  kicker,
  titulo,
  acessorio,
  aberto,
  onAbertoChange,
  testId,
  dataAttrs,
  rodape,
  children,
}: LaudoBlocoRecolhivelProps): JSX.Element {
  const corpoId = useId();
  const rotulo = (aberto ? t.recolher : t.abrir).replace('{{bloco}}', titulo);
  return (
    <Blueprint
      as="section"
      data-testid={testId}
      data-aberto={aberto ? 'sim' : 'nao'}
      {...dataAttrs}
    >
      <div className="flex flex-wrap items-center justify-between gap-sc-2 p-sc-4">
        <div>
          <CardKicker className="block">{kicker}</CardKicker>
          <h2 className="m-0 mt-1 text-heading-sm text-fg">{titulo}</h2>
        </div>
        <div className="flex items-center gap-sc-3">
          {acessorio}
          <button
            type="button"
            aria-expanded={aberto}
            aria-controls={corpoId}
            aria-label={rotulo}
            data-testid={`${testId}-toggle`}
            className="border border-border bg-surface px-2.5 py-1 text-caption text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
            onClick={() => onAbertoChange(!aberto)}
          >
            {aberto ? t.recolherCurto : t.abrirCurto}
          </button>
        </div>
      </div>
      {aberto && (
        <div id={corpoId} className="border-t border-border p-sc-4" data-testid={`${testId}-corpo`}>
          {children}
        </div>
      )}
      {rodape !== undefined && (
        <div className="border-t border-border p-sc-4" data-testid={`${testId}-rodape`}>
          {rodape}
        </div>
      )}
    </Blueprint>
  );
}
